import { roundness } from "@provider/dripsy";
import { useDripsyColor } from "@utils";
import { TextInput, View } from "dripsy";
import * as React from "react";
import type { TextInput as NativeTextInput } from "react-native";
import { Platform } from "react-native";

import IconButton from "./icon-button";
import CrossIcon from "./icons/cross-icon";
import MagnifyIcon from "./icons/magnify-icon";

export const SEARCHBAR_HEIGHT = 46;

// useInternalSearch is used to speed up rendering of searchbar component
function useInternalSearch({ searchQuery, search }: { searchQuery: string | null; search: (s: string) => void }) {
  const [internalSearch, setInternalSearch] = React.useState(searchQuery ?? "");
  const hasEmptySearch = !searchQuery;
  React.useEffect(() => {
    if (hasEmptySearch) {
      setInternalSearch("");
    }
  }, [hasEmptySearch]);
  const setSearch = (v: string) => {
    setInternalSearch(v);
    search(v);
  };
  return [internalSearch, setSearch] as const;
}

function useAutoFocus({ autoFocus }: { autoFocus: boolean }) {
  const inputRef = React.useRef<NativeTextInput>(null);

  React.useEffect(() => {
    if (Platform.OS !== "web") {
      const timeout = setTimeout(() => {
        if (autoFocus) {
          inputRef.current?.focus();
        }
      }, 550);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, []);
  return {
    ref: inputRef,
    autoFocus: Platform.OS === "web" ? autoFocus : undefined,
  };
}

function Searchbar({
  placeholder,
  href,
  searchQuery,
  search,
  autoFocus = false,
}: {
  placeholder: string;
  searchQuery: string | null;
  href?: string;
  search: (s: string) => void;
  autoFocus?: boolean;
}) {
  const autoFocusProps = useAutoFocus({ autoFocus });
  const [internalSearch, setInternalSearch] = useInternalSearch({ searchQuery, search });
  const placeholderColor = useDripsyColor({ color: "$textGrey" });
  const pointerEvents = href ? "none" : "auto";

  return (
    <View
      sx={{
        backgroundColor: "$background",
        height: SEARCHBAR_HEIGHT,
        borderWidth: 2,
        borderColor: "$lightGrey",
        borderRadius: roundness,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingLeft: "$2",
        paddingRight: "$2",
        elevation: 2,
        ...(Platform.OS === "web" ? { pointerEvents } : {}),
      }}
      {...(Platform.OS !== "web" ? { pointerEvents } : {})}
    >
      <View
        sx={{
          marginRight: "$2",
        }}
      >
        <MagnifyIcon size={18} color="$grey" />
      </View>

      <TextInput
        {...autoFocusProps}
        value={internalSearch}
        placeholderTextColor={placeholderColor}
        placeholder={placeholder}
        editable={!href}
        onChangeText={(text) => setInternalSearch(text)}
        sx={{
          color: "$text",
          fontSize: 14,
          flex: 1,
          height: "100%",
          letterSpacing: 0.1,
          fontWeight: "500",
          ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
        }}
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete="off"
        // autoFocus={autoFocus}
      />
      {internalSearch.length > 0 ? (
        <IconButton
          onPress={() => {
            setInternalSearch("");
          }}
          hitslop={15}
        >
          <CrossIcon size={12.5} color="$grey" />
        </IconButton>
      ) : null}
    </View>
  );
}
export default Searchbar;
